import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Avatar from "../../../interfaces/avatar/Avatar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { toast, Id } from "react-toastify";
import { useAuth } from "../../../firebase/AuthProvider";
import EventNotification from "../../../interfaces/eventNotification/EventNotification";
import { selectedConversation$ } from "../../../services/Subjects/ChatSubjects";
import {
  currentConversationId$,
  UpdateSelectedRegionIdFromHeaderSubject,
  UpdateSelectedWaiverIdFromHeaderSubject,
} from "../../../services/Subjects/userSubject";
import {
  Conversation,
  getMyConversations,
  Message,
  markConversationAsRead,
} from "../../../services/chatService/ChatService";
import { useChatConnection } from "../../../contexts/ChatConnectionContext";
import { useSelectedRegion } from "../../../contexts/RegionContext";
import { useNotifications } from "../../../contexts/NotificationProvider";
import { getAvatarsFromStorage } from "../../../services/fileServices/FileService";
import headercalendaricon from "../../../assets/images/headercalendaricon.svg";
import headerchaticon from "../../../assets/images/headerchaticon.svg";
import headerbellicon from "../../../assets/images/headerbellicon.svg";
import avtar from "../../../assets/images/defaultavtarimg.svg";
import maxiziseicon from "../../../assets/images/MaxsimizesImage.svg";
import { Dropdown, Offcanvas } from "react-bootstrap";
import { TeamUser } from "../../../interfaces/team/TeamUser";
import { getTeamBySupervisor } from "../../../services/userServices/TeamService";
import { selectedUserForTask$ } from "../../../services/Subjects/userSubject";
import "./Headercomponent.css";
import ChatPage from "../../components/realtimeChatComponents/ChatPage";
import { ConversationListRef } from "../../components/realtimeChatComponents/ConversationListComponent";

import NotificationPanel from "../../components/notifications/NotificationPanel";
import { useNotificationState } from "../../../contexts/NotificationStateContext";
import useUpdateTitle from "../../../hooks/useUpdateTitle";
import { conversationRead$ } from "../../../hooks/globalChatHooks/useGlobalChat";
import AvatarImage from "../../ui/Avatar/AvatarImage";
import MobileMenu from "./MobileMenu";
import GlobalSearch from "../../components/search/GlobalSearch";
import AddNewBtn from "./AddNewBtn";
import { OptionType } from "../../../interfaces/dynamicForms/FormBuilder";
import { getAllRegions } from "../../../services/userServices/UserInfoService";
import Region from "../../../interfaces/identity/Region";
import Waiver from "../../../interfaces/participant/Waiver";
import { getWaivers } from "../../../services/participantServices/ParticipantService";
import { ROLE_PRIORITY, ROLES } from "../../../Data/SD";
import { HEADER_ROLE_MAP, ROLE_MAP } from "../../components/dashboard/OwnerDashboard/OwnerParticipantsElement";

interface HeaderComponentProps {
  handleShowSidebar?: () => void;
}

// Helper function to convert date to timestamp (same as ConversationListComponent)
const toTimestamp = (
  d: string | number | Date | null | undefined
): number | null => {
  if (!d && d !== 0) return null;
  if (d instanceof Date) return d.getTime();
  if (typeof d === "number") return Number.isFinite(d) ? d : null;
  if (typeof d === "string") {
    const t = Date.parse(d);
    return Number.isFinite(t) ? t : null;
  }
  return null;
};

// Helper function to normalize message (same as ConversationListComponent)
const normalizeMessage = (m: Message): Message & { _ts?: number } => {
  const ts = toTimestamp((m as any).createDate ?? (m as any).createdAt);
  return { ...m, _ts: ts ?? undefined };
};

// Helper function to check if a conversation is unread (same logic as ConversationListComponent)
const isConversationUnread = (
  conv: Conversation,
  currentUserId: string
): boolean => {
  const lastMsg = conv.lastMessage as any | undefined;

  if (
    !lastMsg ||
    (typeof lastMsg === "object" && Object.keys(lastMsg).length === 0)
  ) {
    return false;
  }

  const normalizedMsg = normalizeMessage(lastMsg as Message);
  const lastMsgTs =
    normalizedMsg._ts ??
    toTimestamp((lastMsg as any).createDate ?? (lastMsg as any).createdAt);

  const dateSaysRead =
    !!lastMsgTs &&
    !!conv.lastReadAt &&
    new Date(conv.lastReadAt).getTime() >= lastMsgTs;

  const authoredByMe = normalizedMsg.author?.id === currentUserId;

  const read =
    (typeof conv.unreadCount === "number"
      ? conv.unreadCount === 0
      : !!lastMsg && !!conv.lastReadAt && dateSaysRead) || authoredByMe;

  return !read;
};

// Helper function to get page title from pathname
function splitCamelCase(str: string) {
  return str.replace(/([a-z])([A-Z])/g, "$1 $2");
}

function toTitleCase(str: string) {
  return splitCamelCase(str)
    .replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const getPageTitle = (pathname: string): string => {
  console.log('pathname', pathname)
  // SPECIAL CASE for login
  if (pathname === "/" || pathname === "/login") {
    return "Login";
  }

  if (pathname.startsWith("/profile/user")) {
    return "User Profile";
  }

  // Sidebar navigation items mapping (check these first)
  // Non-developer roles
  if (
    pathname.endsWith("/dashboard/owner") ||
    pathname.endsWith("/dashboard") ||
    pathname.endsWith("/home")
  ) {
    return "Dashboard";
  }
  if (pathname.endsWith("/participant-snapshot")) {
    return "Participant Files";
  }
  if (pathname.startsWith("/tasks") || pathname.startsWith("/task")) {
    return "Tasks";
  }
  if (pathname.includes("/dashboard/owner/participants")) {
    return "Participants";
  }
  if (
    pathname.startsWith("/drive") ||
    pathname.includes("/drive/caseFileDrive")
  ) {
    return "Drive";
  }
  if (pathname.startsWith("/dashboard/owner/team")) {
    return "Team";
  }
  if (pathname.startsWith("/admin-requests")) {
    return "Admin Requests";
  }

  // Developer roles
  if (pathname.startsWith("/issues") || pathname.startsWith("/issue")) {
    return "Issues";
  }
  if (pathname.startsWith("/sprints")) {
    return "Sprints";
  }
  if (pathname.startsWith("/wiki")) {
    return "Wiki Pages";
  }

  // If not in sidebar, use URL-based logic
  const fullTitleOverrides: Record<string, string> = {
    "/drive/employee-files": "Employee Files",
    "/drive/caseFileDrive": "Participant Files",
    "/drive/personal": "Personal Files",
    "/home": "Home",
  };

  if (fullTitleOverrides[pathname]) {
    return fullTitleOverrides[pathname];
  }

  const titleOverrides: Record<string, string> = {
    "/termsservices": "Terms of Service",
    "/privacypolicy": "Privacy Policy",
    "/legal/sign/tos": "Terms of Service",
    "/legal/sign/data-agreement": "Data Processing Agreement",
    "/legal/sign/security-compliance-terms": "Security & Compliance Terms",
    "/legal/sign/electronic-signature-consent": "Electronic Signature Consent",
  };

  const parts = pathname.split("/").filter(Boolean);

  // Convert URL segments
  let formattedParts = parts.map(toTitleCase);

  // SPECIAL CASE: Drive CaseFileDrive → remove duplicate "Drive"
  if (formattedParts[0] === "Drive" && formattedParts[1]?.endsWith("Drive")) {
    formattedParts[1] = formattedParts[1].replace(/Drive$/, "").trim();
  }

  // Join words
  const rawTitle =
    titleOverrides[pathname] ??
    (formattedParts.length ? formattedParts.join(" ") : "Home");

  // Remove notification count like "(3)"
  const cleaned = rawTitle.replace(/^\(\d+\)\s*/, "");

  return cleaned;
};

export default function HeaderComponent({
  handleShowSidebar,
}: HeaderComponentProps) {
  const [showSideChat, setShowSideChat] = useState(false);
  const showSideChatRef = useRef<boolean>(false);

  const handleCloseSideChat = () => {
    setShowSideChat(false);
    showSideChatRef.current = false;
    // Reset conversation ID to 0 when closing side chat so new notifications will show
    currentConversationId$.next(0);
    setSelectedConversationId(0);
  };

  const handleCloseChat = () => {
    setShowSideChat(false);
  };

  const handleChatExited = () => {
    handleShowChatHead(true);
  };
  const handleShowSideChat = () => {
    setShowSideChat(true);
    showSideChatRef.current = true;
  };

  const location = useLocation();
  const navigate = useNavigate();
  const { notify } = useNotifications();
  const { currentUser, currentRoles, currentUserInfo } = useAuth();
  const { notifications, hasUnread } = useNotificationState();
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  useUpdateTitle(totalUnreadCount);

  const [isOpen, setIsOpen] = useState(false);
  const [addToggle, setAddToggle] = useState(false);
  // const [notifications, setNotifications] = useState<EventNotification[]>([]);
  const [taskId, setTaskId] = useState<string>("");
  const [currentAvatar, setCurrentAvatar] = useState<Avatar | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track the initial load
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const { connection } = useChatConnection();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  // const { availableRegions, selectedRegionId, setSelectedRegionId } = useSelectedRegion();
  const [users, setUsers] = useState<TeamUser[] | undefined>(); // List of user who working under current user
  const [selectedUsers, setSelectedUsers] = useState<TeamUser[]>(
    selectedUserForTask$.getValue()
  );
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [showChatHead, setShowChatHead] = useState<boolean>(true);
  const [showUsersDropdown, setShowUsersDropdown] = useState(false);
  // const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const headerRightIconRef = useRef<HTMLUListElement>(null);
  const toastIdMap = useRef<Map<number, Id>>(new Map());
  const [selectedConversationId, setSelectedConversationId] =
    useState<number>(0);
  const [region, setRegion] = useState<Region[]>([]);
  const [waivers, setWaivers] = useState<Waiver[]>([]);
  // Track selected waiver so the toggle shows the chosen name
  const [selectedWaiver, setSelectedWaiver] = useState<Waiver | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);

  // Ref for the dropdown container to detect outside clicks
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null); // Ref for the button itself
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const conversationListRef = useRef<ConversationListRef>(null);
  const unreadConversationsToastId = useRef<Id | null>(null); // Track unread conversations toast

  const getToastSessionKey = (userId: string) => `unreadToastShown_${userId}`;

  const hasShownToastForSession = (userId: string): boolean => {
    return sessionStorage.getItem(getToastSessionKey(userId)) === "true";
  };

  const markToastAsShown = (userId: string) => {
    sessionStorage.setItem(getToastSessionKey(userId), "true");
  };

  const clearToastFlag = (userId: string) => {
    sessionStorage.removeItem(getToastSessionKey(userId));
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Detect overflow in header right icon container
  useEffect(() => {
    if (!headerRightIconRef.current || isMobile) {
      setShowOverflowMenu(false);
      return;
    }

    const checkOverflow = () => {
      const container = headerRightIconRef.current;
      if (!container) return;

      // Check if container's scroll width exceeds its client width
      const isOverflowing = container.scrollWidth > container.clientWidth;

      // Also check if container width exceeds parent available space
      const parent = container.parentElement;
      if (parent) {
        const parentRect = parent.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const availableWidth = parentRect.width - parentRect.width * 0.1; // 10% buffer

        if (containerRect.width > availableWidth || isOverflowing) {
          setShowOverflowMenu(true);
          return;
        }
      }

      setShowOverflowMenu(isOverflowing);
    };

    // Check on mount and resize with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(checkOverflow, 100);
    window.addEventListener("resize", checkOverflow);

    // Use ResizeObserver for more accurate detection
    const resizeObserver = new ResizeObserver(() => {
      setTimeout(checkOverflow, 50);
    });

    if (headerRightIconRef.current) {
      resizeObserver.observe(headerRightIconRef.current);
    }
    if (headerRightIconRef.current?.parentElement) {
      resizeObserver.observe(headerRightIconRef.current.parentElement);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkOverflow);
      resizeObserver.disconnect();
    };
  }, [isMobile, waivers, region, selectedWaiver, selectedRegion]);

  useEffect(() => {
    showSideChatRef.current = showSideChat;
  }, [showSideChat]);

  // Check for unread notifications
  // useEffect(() => {
  //     const unread = notifications.some(n => !n.read);
  //     setHasUnreadNotifications(unread);
  // }, [notifications]);

  // Check for unread messages
  useEffect(() => {
    const unread = conversations.some((c) => (c.unreadCount || 0) > 0);
    setHasUnreadMessages(unread);
  }, [conversations]);

  useEffect(() => {
    if (!currentUser) return;

    const unreadNotifCount = notifications.filter((n) => !n.read).length;
    // Count unread conversations using the same logic as ConversationListComponent
    const unreadConversations = conversations.filter((c) =>
      isConversationUnread(c, currentUser.uid)
    );
    const unreadMsgCount = unreadConversations.length;
    setTotalUnreadCount(unreadNotifCount + unreadMsgCount);
  }, [notifications, conversations, currentUser]);

  // Dismiss unread conversations toast when chat panel is opened and mark as shown
  useEffect(() => {
    if (showSideChat && currentUser) {
      if (unreadConversationsToastId.current) {
        toast.dismiss(unreadConversationsToastId.current);
        unreadConversationsToastId.current = null;
      }
      // Mark toast as shown when user opens chat panel (so it doesn't show again even on refresh)
      markToastAsShown(currentUser.uid);
    }
  }, [showSideChat, currentUser]);

  // Check for unread conversations and show toast if there are any (only once per login session)
  useEffect(() => {
    if (!currentUser) return;

    // Count unread conversations using the same logic as ConversationListComponent
    const unreadConversations = conversations.filter((c) =>
      isConversationUnread(c, currentUser.uid)
    );
    const unreadConvCount = unreadConversations.length;

    // Only show toast once per session: check sessionStorage, there are unread conversations, no active toast, and chat panel is closed
    // sessionStorage persists across page refreshes but clears when browser session ends (perfect for login session)
    if (
      unreadConvCount > 0 &&
      !hasShownToastForSession(currentUser.uid) &&
      !unreadConversationsToastId.current &&
      !showSideChat
    ) {
      const unreadConversationElement = (
        <div
          className="custom-toast-container"
          onClick={() => setShowSideChat(true)}
          style={{ cursor: "pointer" }}
        >
          <div className="toast-header">
            <img
              style={{ width: "1.5rem", height: "1.5rem" }}
              src={headerchaticon}
              alt="headerchaticon"
            />
            <span className="toast-title">Unread Conversations</span>
          </div>
          <p className="toast-body">
            You have {unreadConvCount} pending unread message
            {unreadConvCount > 1 ? "s" : ""} in your conversations.
          </p>
          <div className="toast-actions">
            <button
              className="reply-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowSideChat(true);
              }}
            >
              View Messages
            </button>
          </div>
        </div>
      );
      const toastId = toast(unreadConversationElement, {
        closeOnClick: true,
        closeButton: false,
        hideProgressBar: true,
        autoClose: 5000,
        className: "custom-toast-wrapper",
        onClose: () => {
          unreadConversationsToastId.current = null;
          markToastAsShown(currentUser.uid);
        },
      });
      unreadConversationsToastId.current = toastId;
      // Mark that we've shown the toast immediately in sessionStorage (persists across refreshes)
      markToastAsShown(currentUser.uid);
    }
  }, [conversations, showSideChat, currentUser]);

  useEffect(() => {
    const subscription = conversationRead$.subscribe((readConversationId) => {
      setConversations((prevConvos) =>
        prevConvos.map((convo) =>
          convo.id === readConversationId ? { ...convo, unreadCount: 0 } : convo
        )
      );
    });

    return () => subscription.unsubscribe();
  }, []);

  // Track the currently selected conversation
  useEffect(() => {
    // Get initial value immediately
    setSelectedConversationId(currentConversationId$.getValue());

    const subscription = currentConversationId$.subscribe((conversationId) => {
      setSelectedConversationId(conversationId);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Joining Date formating
  const formatJoiningDate = (dateInput?: string | Date) => {
    if (!dateInput) return "";
    const date =
      typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    const day = date.getDate();
    const suffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
          ? "nd"
          : day % 10 === 3 && day !== 13
            ? "rd"
            : "th";

    return `${date.toLocaleString("en-US", {
      month: "long",
    })} ${day}${suffix}, ${date.getFullYear()}`;
  };

  // --- Click Outside Handler ---
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close if click is outside the button AND outside the dropdown panel
      if (
        isOpen &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
      if (
        showUsersDropdown &&
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target as Node)
      ) {
        setShowUsersDropdown(false);
      }
    };

    // Add listener when dropdown is open
    if (isOpen || showUsersDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    // Cleanup listener on component unmount or when isOpen changes
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, showUsersDropdown]); // Re-run effect when isOpen changes

  // --- Select Handler ---
  // const handleSelectRegion = (regionId: string) => {
  //     setSelectedRegionId(regionId);
  //     setIsOpen(false); // Close dropdown after selection
  // };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleAddMenu = () => {
    setAddToggle(!addToggle);
  };

  useEffect(() => {
    setAddToggle(false);
    setIsPanelOpen(false);

    const extractTaskId = () => {
      const match = location.pathname.match(/\/task\/([^\/]+)/);
      if (match) {
        const id = match[1];
        setTaskId(id);
        // Use taskId for any logic here
      } else {
        setTaskId("");
      }
    };

    extractTaskId();
    // Get notifications on a polling timer
  }, [location]);

  useEffect(() => {
    if (!currentUser) return;
    currentUser.getIdToken().then((token) => {
      if (token) {
        if (
          !currentRoles?.some(
            (role) =>
              role.name === "OWNER" ||
              role.name === "ADMIN" ||
              role.name === "ADMIN"
          )
        ) {
          // getNotificationsForUser(token);
          getData(token);
        }
        getTeam(token);
      }
    });
  }, [currentUser]);

  // const getNotificationsForUser = async (token: string) => {
  //     const response = await getNotifications(token);
  //     if (response !== undefined && response !== null) {
  //         setNotifications(response);
  //     }
  // };

  const getData = async (token: string) => {
    const data = await getMyConversations(token);
    setConversations(data);
  };

  useEffect(() => {
    if (!connection) return;
    conversations.forEach((conv) => {
      connection
        .invoke("JoinConversation", conv.id)
        .then(() => {
          // console.log("Joined conversation group:", conv.id);
        })
        .catch((err) => {
          console.error(`Failed to join conversation ${conv.id}`, err);
        });
    });
  }, [connection, conversations]);

  const handleMarkAsRead = async (conversationId: number) => {
    if (currentUser) {
      const token = await currentUser.getIdToken();
      const response = await markConversationAsRead(token, conversationId);
      if (response.success) {
        setConversations((prev) =>
          prev.map((c) =>
            c.id === conversationId ? { ...c, unreadCount: 0 } : c
          )
        );
        const toastId = toastIdMap.current.get(conversationId);
        if (toastId) {
          toast.dismiss(toastId);
          toastIdMap.current.delete(conversationId);
        }
      }
    }
  };

  const handleReply = (conversationId: number) => {
    selectedConversation$.next(conversationId);

    const toastId = toastIdMap.current.get(conversationId);
    if (toastId) {
      toast.dismiss(toastId);
      toastIdMap.current.delete(conversationId);
    }
    handleShowSideChat();
  };

  useEffect(() => {
    if (
      connection &&
      currentUser &&
      !currentRoles?.some(
        (role) =>
          role.name === "OWNER" ||
          role.name === "ADMIN" ||
          role.name === "ADMIN"
      )
    ) {
      const handleReceiveMessage = (msg: Message) => {
        // if msg.conversationId === conversation.id, push into messages
        if (msg.author.id !== currentUser.uid) {
          // Check if the user is currently viewing the conversation with this sender
          // Read current value directly from observable to avoid stale closure issues
          // Also check if side chat is open - if closed, always show notifications
          const currentConversationId = currentConversationId$.getValue();
          const isSideChatOpen = showSideChatRef.current; // Use ref to get latest value
          const isViewingThisConversation =
            isSideChatOpen &&
            currentConversationId > 0 &&
            currentConversationId === msg.conversationId;

          // Only show notification if not viewing this conversation
          if (!isViewingThisConversation) {
            // Check if a toast already exists for this conversation to prevent duplicates
            const existingToastId = toastIdMap.current.get(msg.conversationId);
            if (!existingToastId) {
              // Only create toast if one doesn't already exist
              playNotificationSound("message_notification.mp3");
              const messageElement = (
                <div
                  className="custom-toast-container"
                  onClick={() => handleReply(msg.conversationId)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="toast-header">
                    <AvatarImage userId={msg.author.id} size={"sm"} />
                    <span className="toast-title">
                      {msg.author.firstName + " " + msg.author.lastName}
                    </span>
                  </div>
                  <p className="toast-body">
                    {msg.attachmentUrl &&
                      msg.attachmentType?.startsWith("image")
                      ? "📷 Sent an image"
                      : msg.attachmentUrl
                        ? `📎 Sent an attachment`
                        : msg.messageContent && msg.messageContent.length > 250
                          ? `${msg.messageContent.substring(0, 250)}...`
                          : msg.messageContent || "Sent a message"}
                  </p>
                  <div className="toast-actions">
                    <button
                      className="mark-as-read-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(msg.conversationId);
                      }}
                    >
                      Mark as Read
                    </button>
                    <button
                      className="reply-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReply(msg.conversationId);
                      }}
                    >
                      Reply
                    </button>
                  </div>
                </div>
              );
              const toastId = toast(messageElement, {
                closeOnClick: true,
                closeButton: false,
                hideProgressBar: true,
                autoClose: 5000,
                className: "custom-toast-wrapper",
                onClose: () => {
                  // Clean up the toast ID when the toast is closed
                  toastIdMap.current.delete(msg.conversationId);
                },
              });
              toastIdMap.current.set(msg.conversationId, toastId);
            } else {
              console.log(
                "Toast already exists for conversation",
                msg.conversationId,
                "- skipping duplicate notification"
              );
            }
          }

          // Always update the conversation state (for unread counts, etc.)
          setConversations((prevConversations) => {
            const newConversations = prevConversations.map((conv) => {
              if (conv.id === msg.conversationId) {
                return {
                  ...conv,
                  // Update the lastMessage to the newly received message
                  lastMessage: msg,
                  // Only increment unread count if not viewing this conversation
                  unreadCount: isViewingThisConversation
                    ? conv.unreadCount || 0
                    : (conv.unreadCount || 0) + 1,
                  // If your Conversation interface has other fields like
                  // 'lastMessageDate', you might update those too. For example:
                  // lastMessageDate: msg.createDate,
                };
              }
              return conv;
            });
            return [...newConversations];
          });
        }
      };

      // Handler for MessageSendResponse (PascalCase event - used for attachments)
      // const handleReceiveMessageFromResponse = (response: MessageSendResponse) => {
      //     // Convert MessageSendResponse to Message format
      //     console.log("Received message from response:", response);

      //     if (!response.author) {
      //         console.warn("Received message without author:", response);
      //         return;
      //     }

      //     // Create a minimal UserInfo object with required fields
      //     // The backend sends a simplified UserInfo, so we provide defaults for missing fields
      //     const author: UserInfo = {
      //         id: response.author.id,
      //         scid: response.author.scId || "",
      //         firstName: response.author.firstName,
      //         lastName: response.author.lastName,
      //         displayName: response.author.displayName,
      //         email: response.author.email,
      //         joinDate: response.author.joinDate ? new Date(response.author.joinDate) : new Date(),
      //         organization: {} as any, // Not provided in MessageSendResponse
      //         roles: [], // Not provided in MessageSendResponse
      //         regions: [], // Not provided in MessageSendResponse
      //     };

      //     const msg: Message = {
      //         id: response.id,
      //         conversationId: response.conversationId,
      //         author: author,
      //         messageContent: response.messageContent,
      //         createDate: new Date(response.createDate),
      //         attachmentUrl: response.attachmentUrl,
      //         attachmentType: response.attachmentType,
      //         messageType: "", // Not provided in MessageSendResponse
      //         reactions: response.reactions?.map(r => ({
      //             userId: r.userId,
      //             reactionType: r.reactionType,
      //         })),
      //     };

      //     // Use the same handler logic
      //     handleReceiveMessage(msg);
      // };

      // Handler for "ReceiveMessage" event (used in chat window)
      // This is ONLY for attachment messages, since text messages are already handled by
      // the notification event handlers (receivemessageforuserasnotification and ReceiveMessageForUserASNotification)
      // Processing text messages here would cause duplicate notifications
      const handleReceiveMessageEvent = (msg: Message) => {
        console.log("ReceiveMessage event fired:", msg);
        // Only process messages with attachments from other users
        // Text messages are handled by the notification event handlers above
        if (msg.author.id !== currentUser.uid && msg.attachmentUrl) {
          console.log(
            "ReceiveMessage event - attachment message detected, processing notification"
          );
          handleReceiveMessage(msg);
        } else {
          console.log(
            "ReceiveMessage event - skipping (text message or from self)"
          );
        }
      };

      // Attach event handlers for both event name formats
      connection.on(
        "receivemessageforuserasnotification",
        handleReceiveMessage
      );
      // connection.on("ReceiveMessageForUserASNotification", handleReceiveMessageFromResponse);
      connection.on("ReceiveMessage", handleReceiveMessageEvent); // This is for attachment messages
      connection.on("NewMessage", async (conversation) => {
        const token = await currentUser.getIdToken();
        getData(token);
      });

      return () => {
        // Clean up
        connection.off(
          "receivemessageforuserasnotification",
          handleReceiveMessage
        );
        // connection.off("ReceiveMessageForUserASNotification", handleReceiveMessageFromResponse);
        connection.off("ReceiveMessage", handleReceiveMessageEvent);
        connection.off("NewMessage");
      };
    }
  }, [currentUser, connection, conversations]);

  // useEffect(() => {

  //     const pollInterval = 30000;

  //     const pollNotifications = async () => {
  //         if (!currentUser) {
  //             return;
  //         }

  //         try {
  //             const authToken = await currentUser.getIdToken();
  //             const latest = await getLatestNotifications(authToken);

  //             if (latest && latest.length > 0) {
  //                 setNotifications((prev) => {
  //                     const allNotifications = [...latest, ...prev];
  //                     const uniqueNotifications = Array.from(new Map(allNotifications.map(item => [item.id, item])).values());
  //                     uniqueNotifications.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  //                     return uniqueNotifications;
  //                 });

  //                 if (!isInitialLoad) {
  //                     playNotificationSound();
  //                 }

  //                 latest.forEach((notification) => {
  //                     let toastOptions: ToastOptions = {};
  //                     switch (notification.status) {
  //                         case "error":
  //                             toastOptions = { type: "error" };
  //                             break;
  //                         case "success":
  //                             toastOptions = { type: "success" };
  //                             break;
  //                         case "info":
  //                         default:
  //                             toastOptions = { type: "info" };
  //                             break;
  //                     }

  //                     const content = (
  //                         <div className="text-xs inter-font">
  //                             {renderMessageWithLinks(notification.message, notification)}
  //                         </div>
  //                     );

  //                 });
  //             }

  //             if (isInitialLoad) {
  //                 setIsInitialLoad(false);
  //             }
  //         } catch (err) {
  //             console.error("Polling failed", err);
  //         }
  //     };

  //     // Initial call
  //     pollNotifications();

  //     // Set interval once
  //     const interval = setInterval(() => {
  //         pollNotifications();
  //     }, pollInterval);

  //     return () => clearInterval(interval); // cleanup

  // }, []); // 👈 empty array so it runs only once on mount

  useEffect(() => {
    if (
      currentUser &&
      !currentRoles?.some(
        (role) => role.name === "OWNER" || role.name === "ADMIN"
      )
    ) {
      const storedAvatars = getAvatarsFromStorage();
      if (storedAvatars) {
        const avatar = storedAvatars.find(
          (avatar) => avatar.id === currentUser.uid
        );
        if (avatar) {
          setCurrentAvatar(avatar);
        }
      }
    }

    GetAllRegions();
    GetWaivers();
  }, [currentUser]);

  useEffect(() => {
    const subscription = selectedUserForTask$.subscribe(setSelectedUsers);
    return () => subscription.unsubscribe();
  }, []);

  const openPanel = () => {
    setIsPanelOpen(true);
  };

  const playNotificationSound = (filename: string | null = null) => {
    let soundFileName;

    if (filename) {
      soundFileName = filename;
    } else {
      // Retrieve the user's preferred sound from local storage
      soundFileName =
        localStorage.getItem("notificationSound") || "notification-4.mp3";
    }

    const audio = new Audio(`/notificationTones/${soundFileName}`); // Assuming the files are in the public folder
    audio
      .play()
      .then(() => { })
      .catch((error) => {
        console.error("Error playing sound:", error);
      });
  };

  const getUserFormattedName = (
    firstName: string | undefined | null,
    lastName: string | undefined | null
  ) => {
    const fName = firstName
      ? `${firstName?.charAt(0)?.toUpperCase()}${firstName?.substring(1)}`
      : "";
    const lName = firstName
      ? `${lastName?.charAt(0)?.toUpperCase()}${lastName?.substring(1)}`
      : "";

    return `${fName} ${lName}` || "";
  };

  const renderMessageWithLinks = (
    message: string,
    notification: EventNotification
  ) => {
    const replacements: { [key: string]: string } = {
      "{task}": "task",
      "{author}": "user",
      "{file}": "file",
      "{activity}": "task_action",
      "{LAUNCHSURVEY}": "LAUNCHSURVEY",
    };

      console.log('Notification', notification)
    const parts = message.split(/(\{[a-zA-Z]+\})/g);
    return parts.map((part, index) => {
      const type = replacements[part];
      const resource = notification.notificationResources?.find(
        (res) => res.resourceType === type
      );
      if (resource) {
        return (
          <Link key={index} to={resource.resourceId}>
            {resource.resourceName}
          </Link>
        );
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };

  const GetAllRegions = async () => {
    const token = await currentUser?.getIdToken();
    if (!token) return;
    getAllRegions(token, true)
      .then((res) => {
        if (res.success) {
          setRegion(res.data);
        } else {
          toast.error(res.message);
        }
      })
      .catch((err) => {
        toast.error(err.message);
      });
  };

  const GetWaivers = async () => {
    if (currentUser) {
      const authToken: string = await currentUser.getIdToken();
      const waiversFetched = await getWaivers(authToken);

      if (waiversFetched.success) {
        setWaivers(waiversFetched.data);
      }
    }
  };

  const getHighestRole = (roles: string[]): string | null => {
    if (!roles || roles.length === 0) return null;

    // 1. Find the raw role name with the highest priority first
    const highestRawRole = roles.reduce((highest, role) => {
      const highestPriority = ROLE_PRIORITY[highest] || 0;
      const currentPriority = ROLE_PRIORITY[role] || 0;
      return currentPriority > highestPriority ? role : highest;
    });

    // 2. Format the result AFTER finding the winner
    // This ensures even single-role arrays get formatted
    return formatRoleName(highestRawRole);
  };

  const formatRoleName = (role: string): string => {
    if (!role) return "";

    const normalizedRole = role.toLowerCase().trim();

    // Return the mapped value if it exists, otherwise return the original string
    return HEADER_ROLE_MAP[normalizedRole] ?? role;
  };

  // Get all the users who user logged user -- including the logged-in user.
  const getTeam = async (token: string) => {
    // We only fetch team if Supervisor, PM, or Owner (depending on your specific permission needs)
    // Adjust this condition if SCs also need to see this list (e.g. for self-selection)
    if (currentUser && currentUserInfo) {
      let combinedUsers: TeamUser[] = [];

      // 1. Create a TeamUser object for the Current Logged-In User
      const currentUserAsTeamMember: TeamUser = {
        id: currentUser.uid,
        firstName: currentUserInfo.firstName || "",
        lastName: currentUserInfo.lastName || "",
        avatar: currentAvatar?.avatar || "", // Use the avatar state we fetched earlier
        // Add other required TeamUser props if any, defaulting to safe values
      };

      // 2. Fetch the team from the backend
      let teamData: TeamUser[] = [];

      // Only fetch team for roles that manage people (Supervisor+)
      if (currentRoles?.some((role) => role.name === "SUPERVISOR" || role.name === "PROGRAM MANAGER")) {
        const team = await getTeamBySupervisor(token);
        if (team) {
          teamData = team;
        }
      }

      // 3. Combine: Current User First, then Team
      const rawList = [currentUserAsTeamMember, ...teamData];

      // 4. Deduplicate based on ID
      const uniqueList = rawList.filter((user, index, self) =>
        index === self.findIndex((u) => (
          u.id === user.id
        ))
      );

      setUsers(uniqueList);
    }
  };

  const toggleUserSelection = (item: TeamUser) => {
    const isSelected = selectedUsers.some((user) => user.id === item.id);
    const newSelectedUsers = isSelected ? [] : [item];
    selectedUserForTask$.next(newSelectedUsers);
  };

  const handleShowChatHead = (show: boolean) => {
    setShowChatHead(show);
  };

  const addConversationButtonRef = useRef<HTMLButtonElement>(null);

  const handleToggleAddConversation = () => {
    if (conversationListRef.current) {
      conversationListRef.current.toggleAddConversation(
        addConversationButtonRef.current
      );
    }
  };

  const renderUserAvatar = (user: TeamUser, isSelected: boolean) => (
    <div
      key={user.id}
      className={`header_filter_img ${isSelected ? "active_profile" : ""}`}
      title={`${user.firstName} ${user.lastName}`}
      onClick={() => toggleUserSelection(user)}
    >
      {user.avatar ? (
        <img
          src={user.avatar || avtar}
          alt={`${user.firstName} ${user.lastName}'s avatar`}
          onError={(e) => {
            e.currentTarget.src = avtar;
          }}
        />
      ) : (
        <>{user.firstName[0] + user.lastName[0]}</>
      )}
    </div>
  );

  const renderUserAvatars = () => {
    if (!users || location.pathname !== "/home") {
      return null;
    }

    if (isMobile) {
      return (
        <div className="header_fillter">
          <div className="relative" ref={userDropdownRef}>
            <div
              className={`header_filter_img plus-icon-container ${showUsersDropdown ? "active" : ""
                }`}
              onClick={() => setShowUsersDropdown(!showUsersDropdown)}
            >
              <FontAwesomeIcon icon={faPlus} className="plus-icon" />
            </div>
            {showUsersDropdown && (
              <div className="absolute right-0 top-full mt-2 w-max rounded-md bg-white shadow-lg z-20">
                {users.map((item) => {
                  const isSelected = selectedUsers.some(
                    (user) => user.id === item.id
                  );
                  return (
                    <div
                      key={item.id}
                      className={`flex items-center p-2 cursor-pointer hover:bg-gray-100 transition-colors ${isSelected ? "active_profile_dropdown" : ""
                        }`}
                      onClick={() => {
                        toggleUserSelection(item);
                        setShowUsersDropdown(false);
                      }}
                    >
                      {item.avatar ? (
                        <img
                          src={item.avatar || avtar}
                          alt=""
                          className="w-8 h-8 rounded-full mr-2"
                          onError={(e) => {
                            e.currentTarget.src = avtar;
                          }}
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full mr-2 flex items-center justify-center bg-gray-200 text-sm font-semibold">
                          {item.firstName[0] + item.lastName[0]}
                        </div>
                      )}
                      <span className="text-sm font-medium">
                        {item.firstName} {item.lastName}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      );
    }

    const visibleUsers = users.slice(0, 5);
    const remainingUsers = users.slice(5);

    return (
      <div className="header_fillter">
        {visibleUsers.map((item) => {
          const isSelected = selectedUsers.some((user) => user.id === item.id);
          return renderUserAvatar(item, isSelected);
        })}

        {remainingUsers.length > 0 && (
          <div className="relative" ref={userDropdownRef}>
            <div
              className={`header_filter_img plus-icon-container ${showUsersDropdown ? "active" : ""
                }`}
              onClick={() => setShowUsersDropdown(!showUsersDropdown)}
            >
              <FontAwesomeIcon icon={faPlus} className="plus-icon" />
            </div>
            {showUsersDropdown && (
              <div className="absolute right-0 top-full mt-2 w-max rounded-md bg-white shadow-lg z-20">
                {remainingUsers.map((item) => {
                  const isSelected = selectedUsers.some(
                    (user) => user.id === item.id
                  );
                  return (
                    <div
                      key={item.id}
                      className={`flex items-center p-2 cursor-pointer hover:bg-gray-100 transition-colors ${isSelected ? "active_profile_dropdown" : ""
                        }`}
                      onClick={() => {
                        toggleUserSelection(item);
                        setShowUsersDropdown(false);
                      }}
                    >
                      {item.avatar ? (
                        <img
                          src={item.avatar || avtar}
                          alt=""
                          className="w-8 h-8 rounded-full mr-2"
                          onError={(e) => {
                            e.currentTarget.src = avtar;
                          }}
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full mr-2 flex items-center justify-center bg-gray-200 text-sm font-semibold">
                          {item.firstName[0] + item.lastName[0]}
                        </div>
                      )}
                      <span className="text-sm font-medium">
                        {item.firstName} {item.lastName}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="main_header">
        {/* Logo */}
        <div className="header_search">
          <div className="sidebar_toggle" onClick={handleShowSidebar}>
            <span></span>
            <span></span>
            <span></span>
          </div>
          {/* <div className="user_name">
                        <h1>{currentUserInfo?.displayName}</h1>
                        <p>{formatJoiningDate(new Date())}</p>

                    </div> */}
          <div className=" user_name overflow-hidden">
            <h1>{getPageTitle(location.pathname)}</h1>
          </div>
          <div className="header_task_search hide-on-desktop">
            {/* <TaskSearch /> */}
            <GlobalSearch />
          </div>
          {renderUserAvatars()}
        </div>

        <div className="header_right_side">
          {/* Navigation Links (Desktop) */}
          <ul className="header_right_icon flex d-flex align-middle justify-center items-center">
            {currentRoles?.some((role) => role.name === "DEVELOPER") && (
              <li className="calender_mobile hide-on-mobile">
                {/* <Link to="/wiki" className="btn btn-primary btn-sm">Create Wiki Page</Link> */}
              </li>
            )}

            <div className="header_task_search hide-on-mobile">
              {/* <TaskSearch /> */}
              <GlobalSearch />
            </div>

            <li
              className={`calender_mobile hide-on-mobile ${showOverflowMenu ? "hide-on-overflow" : ""
                }`}
            >
              <AddNewBtn />
            </li>
            <li
              className={`calender_mobile hide-on-mobile ${showOverflowMenu ? "hide-on-overflow" : ""
                }`}
            >
              <Link to="/calendar">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="19"
                  height="19"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M8.00098 1.99976V5.99976"
                    stroke="black"
                    stroke-width="1.52381"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M15.999 1.99976V5.99976"
                    stroke="black"
                    stroke-width="1.52381"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z"
                    stroke="black"
                    stroke-width="1.52381"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M3 10H21"
                    stroke="black"
                    stroke-width="1.52381"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </Link>
            </li>
            <li
              className={`shrink-0 z-[9] d-flex align-middle items-center justify-center ${showOverflowMenu ? "hide-on-overflow" : ""
                }`}
            >
              <div className="notification_dropdown">
                <div
                  className={`notification-icon hide-on-mobile cursor-pointer ${showNotificationPanel ? "notification-icon-active" : ""
                    }`}
                  onClick={() => setShowNotificationPanel(true)}
                >
                  {showNotificationPanel && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="2.5rem"
                      height="2.5rem"
                      viewBox="0 0 40 40"
                      fill="none"
                      className="notification-icon-background"
                    >
                      <circle cx="20" cy="20" r="20" fill="#FFF" />
                    </svg>
                  )}
                  <img
                    style={{ width: "1.5rem", height: "1.5rem" }}
                    src={headerbellicon}
                    alt="headerbellicon"
                  />
                  {hasUnread && <span className="notification-dot"></span>}
                </div>
              </div>
            </li>
            <li
              className={`shrink-0  hide-on-mobile ${showOverflowMenu ? "hide-on-overflow" : ""
                }`}
            >
              {/* <Link to="/chat"><img src={headerchaticon} alt="headerchaticon" /></Link> */}
              <span
                className="cursor-pointer notification-icon"
                onClick={handleShowSideChat}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M16.2147 1.55267C15.3717 1.48438 14.322 1.48438 12.9843 1.48438H9.01543C7.67782 1.48438 6.62803 1.48438 5.78507 1.55267C4.92651 1.62389 4.21721 1.76829 3.57524 2.0961C2.51958 2.63396 1.66131 3.49223 1.12345 4.5479C0.795631 5.18987 0.651236 5.89916 0.580989 6.75773C0.511719 7.60068 0.511719 8.65048 0.511719 9.98808V14.1463C0.511719 14.7257 0.625835 15.2994 0.847552 15.8346C1.06927 16.3699 1.39424 16.8563 1.80392 17.2659C2.63131 18.0933 3.75348 18.5581 4.92358 18.5581H5.50994C5.75288 18.5581 5.91874 18.803 5.82898 19.0284C5.15871 20.7026 7.08756 22.2168 8.55492 21.169L11.1023 19.3494L11.1511 19.3152C11.8425 18.828 12.6664 18.5638 13.5122 18.5581H14.2498C15.761 18.5581 16.682 18.5581 17.4567 18.3318C18.359 18.0672 19.1803 17.5802 19.8452 16.9153C20.5101 16.2504 20.9972 15.4291 21.2617 14.5268C21.4881 13.7521 21.4881 12.8311 21.4881 11.3198V9.98808C21.4881 8.65048 21.4881 7.60068 21.4198 6.75773C21.3485 5.89916 21.2041 5.18987 20.8763 4.5479C20.3385 3.49223 19.4802 2.63396 18.4245 2.0961C17.7826 1.76829 17.0733 1.62292 16.2147 1.55267ZM4.23965 3.40054C4.64064 3.19565 5.13627 3.07565 5.9041 3.01321C6.68071 2.94979 7.67002 2.94882 9.0486 2.94882H12.9512C14.3298 2.94882 15.3191 2.94882 16.0957 3.01321C16.8635 3.07565 17.3591 3.19663 17.7611 3.40054C18.5413 3.7983 19.1755 4.43287 19.5729 5.21328C19.7778 5.61427 19.8978 6.1099 19.9602 6.87773C20.0236 7.65435 20.0246 8.64365 20.0246 10.0222V11.1774C20.0246 12.877 20.0168 13.5697 19.8568 14.116C19.6612 14.7828 19.3012 15.3897 18.8099 15.881C18.3186 16.3723 17.7117 16.7323 17.045 16.9278C16.4986 17.0878 15.8059 17.0956 14.1063 17.0956H13.5024C12.3578 17.1031 11.2428 17.4606 10.3072 18.1201L7.70514 19.9787C7.4261 20.1777 7.06024 19.8909 7.18805 19.5728C7.29781 19.2987 7.33867 19.0018 7.30704 18.7082C7.27542 18.4146 7.17228 18.1332 7.00666 17.8887C6.84103 17.6442 6.61798 17.4441 6.35704 17.3058C6.09611 17.1676 5.80524 17.0954 5.50994 17.0956H4.92358C4.14162 17.0956 3.39168 16.785 2.83875 16.2321C2.28582 15.6792 1.97518 14.9292 1.97518 14.1473V10.0213C1.97518 8.64267 1.97518 7.65337 2.03958 6.87676C2.10202 6.10893 2.223 5.6133 2.42691 5.21231C2.8245 4.43175 3.45909 3.79814 4.23965 3.40054Z"
                    fill="#212121"
                  />
                  <path
                    d="M8.07238 10.0206C8.07238 10.2793 7.96959 10.5275 7.78662 10.7104C7.60365 10.8934 7.35549 10.9962 7.09674 10.9962C6.83798 10.9962 6.58982 10.8934 6.40685 10.7104C6.22388 10.5275 6.12109 10.2793 6.12109 10.0206C6.12109 9.76181 6.22388 9.51365 6.40685 9.33068C6.58982 9.14771 6.83798 9.04492 7.09674 9.04492C7.35549 9.04492 7.60365 9.14771 7.78662 9.33068C7.96959 9.51365 8.07238 9.76181 8.07238 10.0206ZM11.975 10.0206C11.975 10.2793 11.8722 10.5275 11.6892 10.7104C11.5062 10.8934 11.2581 10.9962 10.9993 10.9962C10.7406 10.9962 10.4924 10.8934 10.3094 10.7104C10.1265 10.5275 10.0237 10.2793 10.0237 10.0206C10.0237 9.76181 10.1265 9.51365 10.3094 9.33068C10.4924 9.14771 10.7406 9.04492 10.9993 9.04492C11.2581 9.04492 11.5062 9.14771 11.6892 9.33068C11.8722 9.51365 11.975 9.76181 11.975 10.0206ZM15.8775 10.0206C15.8775 10.2793 15.7747 10.5275 15.5918 10.7104C15.4088 10.8934 15.1606 10.9962 14.9019 10.9962C14.6431 10.9962 14.395 10.8934 14.212 10.7104C14.029 10.5275 13.9262 10.2793 13.9262 10.0206C13.9262 9.76181 14.029 9.51365 14.212 9.33068C14.395 9.14771 14.6431 9.04492 14.9019 9.04492C15.1606 9.04492 15.4088 9.14771 15.5918 9.33068C15.7747 9.51365 15.8775 9.76181 15.8775 10.0206Z"
                    fill="#212121"
                  />
                </svg>
                {hasUnreadMessages && (
                  <span className="notification-dot"></span>
                )}
              </span>
            </li>

            {currentRoles?.some((role) => role.name == ROLES.OWNER) &&
              location.pathname.endsWith("/dashboard/owner") && (
                <>
                  <li
                    className={`calender_mobile hide-on-mobile ${showOverflowMenu ? "hide-on-overflow" : ""
                      }`}
                  >
                    <Dropdown>
                      <Dropdown.Toggle className="h_d_dropdown" variant="light">
                        <span>
                          {selectedWaiver
                            ? selectedWaiver.waiverName
                            : "Waiver"}
                        </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="7"
                          viewBox="0 0 12 7"
                          fill="none"
                        >
                          <path
                            d="M0 0.7L5.66 6.36L11.32 0.7L10.61 0L5.66 4.95L0.71 0L0 0.7Z"
                            fill="black"
                          />
                        </svg>
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        {waivers.map((w) => (
                          <Dropdown.Item
                            key={w.id}
                            active={selectedWaiver?.id === w.id}
                            onClick={() => {
                              setSelectedWaiver(w);
                              UpdateSelectedWaiverIdFromHeaderSubject(w.id);
                            }}
                          >
                            {w.waiverName}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </li>
                  <li
                    className={`calender_mobile hide-on-mobile ${showOverflowMenu ? "hide-on-overflow" : ""
                      }`}
                  >
                    <Dropdown className="">
                      <Dropdown.Toggle className="h_d_dropdown" variant="light">
                        <span>{selectedRegion?.regionName || "Region"}</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="7"
                          viewBox="0 0 12 7"
                          fill="none"
                        >
                          <path
                            d="M0 0.7L5.66 6.36L11.32 0.7L10.61 0L5.66 4.95L0.71 0L0 0.7Z"
                            fill="black"
                          />
                        </svg>
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        {region.map((r) => (
                          <Dropdown.Item
                            key={r.regionId}
                            active={r.regionId === selectedRegion?.regionId}
                            onClick={() => {
                              setSelectedRegion(r);
                              UpdateSelectedRegionIdFromHeaderSubject(
                                r.regionId
                              );
                            }}
                          >
                            {r.regionName}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </li>
                </>
              )}

            <li className="flex items-center region_mobile d-none">
              <div
                className="relative cursor-pointer hover:text-gray-400 flex justify-center align-middle items-center rounded-full"
                onClick={openPanel}
              ></div>
            </li>
          </ul>
          <NotificationPanel
            isOpen={showNotificationPanel}
            onClose={() => setShowNotificationPanel(false)}
            notifications={notifications}
          />
          <MobileMenu
            hasUnread={hasUnread}
            hasUnreadMessages={hasUnreadMessages}
            onShowChat={handleShowSideChat}
            onShowNotifications={() => setShowNotificationPanel(true)}
            showOnDesktop={showOverflowMenu}
          />
          <Link to={"/profile"} className="no-underline">
            <div className="nw_user_profile_header">
              <div className="nw_user_name hide-on-mobile">
                <h3 className="text-[0.925rem] font-semibold mb-[1px]">
                  {getUserFormattedName(
                    currentUserInfo?.firstName,
                    currentUserInfo?.lastName
                  )}
                </h3>
                <p
                  className="text-[0.875rem] mb-0 text-[#747474]"
                  style={{ fontWeight: 400 }}
                >
                  {currentUserInfo?.roles && currentUserInfo?.roles?.length > 0
                    ? getHighestRole(currentUserInfo.roles.map((r) => r.name))
                    : "N/A"}
                </p>
              </div>
              <figure className="nw_user_profile m-0">
                {currentUser && (
                  <AvatarImage userId={currentUser?.uid} size="sm" />
                )}
              </figure>
            </div>
            {/* <div className='header_profile'>
                            {currentAvatar ? (
                                <img src={`data:image/png;base64,${currentAvatar.avatar}`} />
                            ) : (
                                <img src={dummyimg} alt="dummyimg" />
                            )} 
                            {currentUser && <AvatarImage userId={currentUser?.uid} />}
                        </div>*/}
          </Link>
        </div>
      </div>
      {/* off canvas chat */}
      <Offcanvas
        show={showSideChat}
        onHide={handleCloseSideChat}
        onExited={handleChatExited}
        placement="end"
        className="my_offcanvas my_offcanvas_chat"
      >
        {showChatHead && (
          // <Offcanvas.Header closeButton>
          //   <div className="flex">
          //     <Offcanvas.Title>
          //       <div className="w-[100%]">
          //         <p className="mb-0 text-white">Chat</p>
          //         <button
          //           ref={addConversationButtonRef}
          //           onClick={handleToggleAddConversation}
          //           style={{ marginLeft: "10px" }}
          //         >
          //           <FontAwesomeIcon icon={faPlus} />{""}
          //         </button>
          //       </div>
          //     </Offcanvas.Title>

          //     <button>
          //       <img
          //         style={{ width: "1.1rem" }}
          //         src={maxiziseicon}
          //         alt="maxiziseicon"
          //         onClick={() => {
          //           handleCloseSideChat();
          //           navigate("/chat");
          //         }}
          //       />
          //     </button>
          //   </div>
          // </Offcanvas.Header>

          <Offcanvas.Header className="bg-[#7B5CD6] px-4 py-3">
            <div className="flex w-full items-center justify-between">
              {/* Left: Title */}
              <Offcanvas.Title className="text-white text-lg font-semibold">
                Chat
              </Offcanvas.Title>

              {/* Right: Actions */}
              <div className="flex items-center gap-4">
                {/* Maximize */}
                <button
                  onClick={() => {
                    handleCloseSideChat();
                    navigate("/chat");
                  }}
                  className="text-white hover:opacity-80"
                >
                  <img
                    style={{ width: "1.1rem" }}
                    src={maxiziseicon}
                    alt="maxiziseicon"
                    onClick={() => {
                      handleCloseSideChat();
                      navigate("/chat");
                    }}
                  />
                </button>

                {/* Close (Bootstrap close button styled white) */}
                <button
                  onClick={handleCloseSideChat}
                  className="text-white text-xl leading-none"
                >
                  ✕
                </button>
              </div>
            </div>
          </Offcanvas.Header>

        )}
        <Offcanvas.Body>
          <ChatPage
            ref={conversationListRef}
            isSideView={true}
            handleShowChatHead={handleShowChatHead}
            showHeader={false}
            handleCloseChat={handleCloseChat}
            handleToggleAddConversation={handleToggleAddConversation}
            addConversationButtonRef={addConversationButtonRef}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}
